/**
 * Where the reading pane sits relative to the list it opens.
 *
 * Two placements, the same two every mail client has offered for twenty years:
 *
 *   right    the list on the left, the open item beside it. Three columns.
 *   bottom   the list across the top, the open item underneath it. Two rows.
 *
 * Neither is better; they answer different questions. Beside the list is right
 * for triage - the subject lines stay in view while the reply is read, and the
 * eye travels a short way. Under the list is right for reading: a letter gets
 * the full width of the screen instead of a column, which matters for the two
 * things mail actually contains that do not reflow, quoted threads and HTML
 * newsletters built on a 600px table.
 *
 * Remembered rather than asked again, for the same reason the density is: it is
 * a habit, not a decision. See `density.ts` - this file is deliberately its
 * twin, down to the module-level store, and the two should stay that way.
 *
 * WHY A SEPARATE STORE AND NOT A SECOND FIELD ON THE DENSITY ONE: the density
 * is one setting for both workspace screens, mail and calls, because they are
 * the same shape and an operator who works compact works compact. The reading
 * pane is not that. A call's detail pane is a transcript read top to bottom
 * beside a list of short rows, and stacking it would be a worse screen, not a
 * different one. So this is the mail screen's setting, stored under the mail
 * screen's key, and the calls screen simply never asks for it.
 */
import { useCallback, useSyncExternalStore } from "react";

export type ReadingPane = "right" | "bottom";

const STORAGE_KEY = "crm.mail.readingPane";

function read(): ReadingPane {
  if (typeof window === "undefined") return "right";
  return window.localStorage.getItem(STORAGE_KEY) === "bottom" ? "bottom" : "right";
}

/** Read once at module load so the first paint is already the right shape.
 *  Reading it in an effect instead paints three columns and re-flows a frame
 *  later, which on this screen is the whole workspace jumping. */
let placement: ReadingPane = read();
const listeners = new Set<() => void>();

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function snapshot(): ReadingPane {
  return placement;
}

function set(next: ReadingPane): void {
  if (next === placement) return;
  placement = next;
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next);
  for (const fn of listeners) fn();
}

/** The setting plus the switch, for the screen that owns the toggle. */
export function useReadingPane(): [ReadingPane, (next: ReadingPane) => void] {
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);
  return [value, useCallback((next: ReadingPane) => set(next), [])];
}
